import React, { useState, useEffect } from 'react';
import {
	Card,
	CardBody,
	CardHeader,
} from '../../../../_metronic/_partials/controls';
import {
	Button,
	TextField,
	MuiThemeProvider,
	createMuiTheme,
	FormControlLabel,
	Checkbox
} from '@material-ui/core';
import { useHistory, useParams } from 'react-router-dom';
import {
	deleteTrainerSpecialization,
	getTrainerSpecializationById,
	postTrainerSpecialization,
	updateTrainerSpecialization,
} from '../../../../api/trainerSpecialization';
import { useSkeleton } from '../../../hooks/useSkeleton';
import { alertError, alertSuccess } from '../../../../utils/logger';
import ConfirmDialog from '../../../components/dialogs/ConfirmDialog';
import { shallowEqual, useSelector } from 'react-redux';
import MultilanguageTabBlock from '../../../components/MultilanguageTabBlock';
import { checkIsEmpty } from '../../../../utils/helpers';

// Create theme for delete button (red)
const theme = createMuiTheme({
	palette: {
		secondary: {
			main: '#F64E60',
		},
	},
});

function getEmptyTrainerSpecialization() {
	return {
		fullName: {},
		active: true
	};
}

export default function EditTrainerSpecializationsPage() {
	const [trainerSpecialization, setTrainerSpecialization] = useState(getEmptyTrainerSpecialization());
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const trainerSpecializationId = useParams().id;
	const history = useHistory();
	const user = useSelector((store) => store.authentication?.user, shallowEqual)

	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	useEffect(() => {
		if (!trainerSpecializationId) {
			disableLoadingData();
			return;
		}
		getTrainerSpecializationById(trainerSpecializationId)
			.then((res) => {
				if (res.status === 200) {
					setTrainerSpecialization(res.data);
					disableLoadingData();
				}
			})
			.catch((error) => {
				alertError({ error: error, customMessage: 'Could not get trainer specialization.' });
				history.push('/trainer-specializations');
			});
	}, [trainerSpecializationId, disableLoadingData, history]);

	function saveTrainerSpecialization() {
		if (checkIsEmpty(trainerSpecialization.fullName)) return alertError({ error: null, customMessage: 'The name is required in at least one of the languages.' })
		if (!trainerSpecializationId) {
			postTrainerSpecialization(trainerSpecialization)
				.then((res) => {
					if (res.status === 201) {
						alertSuccess({
							title: 'Saved!',
							customMessage: 'Trainer specialization successfully created.',
						});
						history.push('/trainer-specializations');
					}
				})
				.catch((error) => {
					alertError({ error: error, customMessage: 'Could not save trainer specialization.' });
				});
		} else {
			updateTrainerSpecialization(trainerSpecializationId, trainerSpecialization)
				.then((res) => {
					if (res.status === 200) {
						alertSuccess({
							title: 'Saved!',
							customMessage: 'Changes successfully saved.',
						});
						history.push('/trainer-specializations');
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: 'Could not save changes.',
					});
				});
		}
	}

	const handleChange = (element, lang) => (event) => {
		if (event.target.value === ' ') return
		if (lang) {
			if (!trainerSpecialization[element]) trainerSpecialization[element] = {}
			let newText = trainerSpecialization[element]
			newText[lang] = event.target.value
			setTrainerSpecialization({ ...trainerSpecialization, [element]: newText })
		} else setTrainerSpecialization({ ...trainerSpecialization, [element]: event.target.value });
	};

	const renderMultilanguageTabContent = (lang) => {
		return <>
			<br/>
			<TextField
				id={`fullName`}
				label='Full name'
				value={(trainerSpecialization.fullName && trainerSpecialization.fullName[lang]) || ''}
				onChange={handleChange('fullName', lang)}
				InputLabelProps={{
					shrink: true,
				}}
				margin='normal'
				variant='outlined'
				required
			/>
		</>
	}

	if (isLoadingData) return <ContentSkeleton />;
	else
		return (
			<>
				<Card>
					<CardHeader title='Edit trainer specialization'></CardHeader>
					<CardBody>
						<MultilanguageTabBlock
							multilanguageTabContent = {renderMultilanguageTabContent}
						/>
						<br/>
						<FormControlLabel
							control={<Checkbox checked={trainerSpecialization.active} onChange={() => setTrainerSpecialization({ ...trainerSpecialization, active: !trainerSpecialization.active })} name="checkActive" />}
							label="Active"
						/>
					</CardBody>
				</Card>
				<div style={{ display: 'flex', flexDirection: 'row' }}>
					<Button
						onClick={() => history.push('/trainer-specializations')}
						variant='outlined'
						style={{ marginRight: '20px' }}>
						Back
					</Button>
					<Button
						onClick={() => saveTrainerSpecialization()}
						variant='outlined'
						color='primary'
						style={{ marginRight: '20px' }}>
						Save trainer specialization
					</Button>
					{trainerSpecializationId && user?.role.includes('admin') && (
						<>
							<MuiThemeProvider theme={theme}>
								<Button
									onClick={() => setOpenConfirmDialog(true)}
									variant='outlined'
									color='secondary'
									style={{ marginRight: '20px' }}>
									Delete trainer specialization
								</Button>
								<div
									style={{
										display: 'flex',
										flexDirection: 'row',
										marginLeft: 'auto',
									}}></div>
							</MuiThemeProvider>

							<ConfirmDialog
								title={'Are you sure you want to delete this trainer specialization?'}
								open={openConfirmDialog}
								setOpen={setOpenConfirmDialog}
								onConfirm={() => {
									deleteTrainerSpecialization(trainerSpecializationId)
										.then((res) => {
											if (res.status === 204 || res.status === 200) {
												alertSuccess({
													title: 'Deleted!',
													customMessage: 'Trainer specialization deleted successfully',
												});
												history.push('/trainer-specializations');
											}
										})
										.catch((error) => {
											alertError({
												error: error,
												customMessage: 'Could not delete trainer specialization.',
											});
										});
								}}
							/>
						</>
					)}
				</div>
			</>
		);
}
